import React, { useEffect, useState } from 'react';
import styles from './AIPlanPanel.module.css';

interface Position {
  row: number;
  col: number;
}

interface PlanMove {
  from: Position;
  to: Position;
  pieceType?: string;
  isCapture: boolean;
}

interface AIPlanDetail {
  constellation: string;
  tutorialIndex: number;
  model?: string;                // AI model name (e.g. 'Sentinel')
  ai: PlanMove | null;
  expectedHumanResponse: PlanMove | null;
  evaluation: number;            // Absolute Light − Dark (positive favors Light)
  rawEvaluation?: number;        // AI-perspective score (positive = good for AI)
  aiSide?: 'Light' | 'Dark';     // Which side the AI was playing
  reasoning?: string;
  pvLength: number;
  scripted?: boolean;
  triggerFEN?: string;
  responseFEN?: string;
  currentFEN?: string;
  overrideTriggers?: string[];
}

// Render two FENs side-by-side, highlighting characters that differ. Helps
// spot subtle mismatches like a missing penguin direction arrow.
function FenDiff({ live, candidate }: { live: string; candidate: string }) {
  const max = Math.max(live.length, candidate.length);
  const liveChars: React.ReactNode[] = [];
  const candChars: React.ReactNode[] = [];
  for (let i = 0; i < max; i++) {
    const a = live[i] ?? '';
    const b = candidate[i] ?? '';
    const same = a === b;
    liveChars.push(
      <span key={i} className={same ? styles.same : styles.diff}>
        {a || ' '}
      </span>
    );
    candChars.push(
      <span key={i} className={same ? styles.same : styles.diff}>
        {b || ' '}
      </span>
    );
  }
  return (
    <>
      <div className={styles.fenLine}>
        <span className={styles.fenTag}>live </span>
        <span className={styles.fenContent}>{liveChars}</span>
      </div>
      <div className={styles.fenLine}>
        <span className={styles.fenTag}>trig </span>
        <span className={styles.fenContent}>{candChars}</span>
      </div>
    </>
  );
}

function algebraic(p: Position): string {
  return String.fromCharCode(65 + p.col) + (p.row + 1);
}

// Mirror of normalizeFEN in TutorialOverride.ts — the matcher applies the
// same transforms before comparing. Stripping the `d` turn-prefix and
// collapsing king crowns / ability sparkles keeps the diff view honest:
// only the characters that actually affect matching get highlighted.
function normalizeFenForDisplay(fen: string): string {
  if (!fen) return fen;
  return fen
    .replace(/^d/, '')
    .replace(/♕/g, 'k')
    .replace(/♛/g, 'K')
    .replace(/✳️/g, '*')
    .replace(/⭐/g, '*')
    .replace(/🌟/g, '*');
}

function formatMove(m: PlanMove): string {
  const piece = m.pieceType ?? 'Piece';
  const arrow = m.isCapture ? '×' : '→';
  return `${piece} ${algebraic(m.from)} ${arrow} ${algebraic(m.to)}`;
}

const AIPlanPanel: React.FC = () => {
  const [plan, setPlan] = useState<AIPlanDetail | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('tutorialAIPlanCollapsed') === 'true';
  });

  useEffect(() => {
    const onPlan = (e: Event) => {
      const detail = (e as CustomEvent<AIPlanDetail>).detail;
      if (detail) setPlan(detail);
    };
    const onClear = () => setPlan(null);
    window.addEventListener('tutorialAIPlan', onPlan);
    window.addEventListener('tutorialAIPlanClear', onClear);
    return () => {
      window.removeEventListener('tutorialAIPlan', onPlan);
      window.removeEventListener('tutorialAIPlanClear', onClear);
    };
  }, []);

  if (!plan) return null;

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('tutorialAIPlanCollapsed', next ? 'true' : 'false');
  };

  const evalSign = plan.evaluation > 0 ? '+' : '';
  const evalLabel = plan.evaluation > 0 ? 'Light' : plan.evaluation < 0 ? 'Dark' : 'Even';

  return (
    <div className={`${styles.panel} ${collapsed ? styles.collapsed : ''}`}>
      <button className={styles.header} onClick={toggle} type="button">
        <span className={styles.title}>
          AI plan{plan.model ? ` — ${plan.model}` : ''}
        </span>
        <span className={styles.chevron}>{collapsed ? '▾' : '▴'}</span>
      </button>
      {!collapsed && (
        <div className={styles.body}>
          {/* FEN comparison block — always shown when we have a live FEN and
              there are any overrides for this tutorial. Lets you eyeball the
              exact reason an override didn't match. */}
          {plan.currentFEN && plan.overrideTriggers && plan.overrideTriggers.length > 0 && (() => {
            // Normalize everything once so diffs only reflect matter-for-match
            // differences (no spurious `d` prefix or king crown highlights).
            const liveNorm = normalizeFenForDisplay(plan.currentFEN);
            return (
              <details className={styles.fenDetails}>
                <summary className={styles.fenSummary}>
                  FEN check ({plan.overrideTriggers.length} trigger{plan.overrideTriggers.length === 1 ? '' : 's'})
                </summary>
                <div className={styles.fenBlock}>
                  <div className={styles.fenLine}>
                    <span className={styles.fenTag}>live </span>
                    <span className={styles.fenContent}>{liveNorm}</span>
                  </div>
                  {plan.overrideTriggers.map((trig, i) => {
                    const trigNorm = normalizeFenForDisplay(trig);
                    const matched = trigNorm === liveNorm;
                    return (
                      <div key={i} className={styles.fenCandidate}>
                        <div className={styles.fenCandidateHeader}>
                          <span>#{i + 1}</span>
                          <span className={matched ? styles.fenMatched : styles.fenMissed}>
                            {matched ? '✓ match' : '✗ no match'}
                          </span>
                        </div>
                        {matched ? (
                          <div className={styles.fenLine}>
                            <span className={styles.fenTag}>trig </span>
                            <span className={styles.fenContent}>{trigNorm}</span>
                          </div>
                        ) : (
                          <FenDiff live={liveNorm} candidate={trigNorm} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </details>
            );
          })()}

          {plan.scripted ? (
            <>
              <div className={styles.row}>
                <span className={styles.label}>Played</span>
                <span className={`${styles.value} ${styles.scripted}`}>Scripted move</span>
              </div>
              {plan.triggerFEN && (
                <div className={`${styles.row} ${styles.muted}`}>
                  <span className={styles.label}>Trigger FEN</span>
                  <span className={styles.fen}>{plan.triggerFEN}</span>
                </div>
              )}
              {plan.responseFEN && (
                <div className={`${styles.row} ${styles.muted}`}>
                  <span className={styles.label}>Response FEN</span>
                  <span className={styles.fen}>{plan.responseFEN}</span>
                </div>
              )}
              <div className={styles.meta}>
                <span>override match · no AI eval</span>
              </div>
            </>
          ) : (
            <>
              <div className={styles.row}>
                <span className={styles.label}>Played</span>
                <span className={styles.value}>{plan.ai ? formatMove(plan.ai) : '—'}</span>
              </div>
              {plan.expectedHumanResponse ? (
                <div className={styles.row}>
                  <span className={styles.label}>Expects you</span>
                  <span className={`${styles.value} ${styles.expected}`}>
                    {formatMove(plan.expectedHumanResponse)}
                  </span>
                </div>
              ) : (
                <div className={`${styles.row} ${styles.muted}`}>
                  <span className={styles.label}>Expects you</span>
                  <span className={styles.value}>—</span>
                </div>
              )}
              <div className={styles.meta}>
                <span>
                  eval {evalSign}
                  {Math.round(plan.evaluation)} {evalLabel !== 'Even' ? `(favors ${evalLabel})` : ''}
                </span>
                <span className={styles.dot}>·</span>
                <span>{plan.pvLength}-ply line</span>
              </div>
              {plan.rawEvaluation !== undefined && plan.aiSide && (
                <div className={styles.meta}>
                  <span>
                    AI ({plan.aiSide}) sees {plan.rawEvaluation > 0 ? '+' : ''}
                    {Math.round(plan.rawEvaluation)} from its POV
                  </span>
                </div>
              )}
              {plan.reasoning && <div className={styles.reasoning}>{plan.reasoning}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIPlanPanel;
